import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Drawer from "@mui/material/Drawer";
import { IconX } from "@tabler/icons-react";
import { axiosInstance } from "../../utils/axiosInstance";
import PageContainer from "../layouts/PageContainer";
import {
  Card,
  CollapsibleFilters,
  EmptyState,
  ErrorBanner,
  LoadingPanel,
  PageHeader,
  PHONE_QUERY,
  Pill,
  Tone,
  activatable,
  useMediaQuery,
} from "../ui";
import { Location } from "../../interfaces/interfaces";
import "./customerservice.scss";

interface SalesRecord {
  id: string | number;
  transactionTime: string;
  site?: string;
  device_id: string;
  machine_name: string;
  amount: number;
  currency: string;
  tenderType: string;
  status: string;
  last4: string;
  authCode: string;
  receiptNo: string;
}

interface BackendResponse {
  items: SalesRecord[];
  total: number;
  page: number;
  pageSize: number;
  sort: string;
  filters: any;
}

interface Filters {
  from: string;
  to: string;
  last4: string;
  /** Locations.locationPin (SQGSXX) */
  siteId: string;
  deviceId: string;
  minAmount: string;
  maxAmount: string;
  page: number;
  pageSize: number;
  sort: string;
}

interface SiteOption {
  name: string;
  locationPin: string;
}

function salesStatusTone(status: string): Tone {
  const s = status.toLowerCase();
  if (s === "approved") return "good";
  if (s === "pending") return "warn";
  if (s === "failed" || s === "declined") return "bad";
  return "neutral";
}

const CustomerServiceSales = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isPhone = useMediaQuery(PHONE_QUERY);

  const getDefaultDates = () => {
    const today = new Date();
    const to = new Date(today);
    to.setHours(0, 0, 0, 0);
    const from = new Date(to);
    from.setDate(from.getDate() - 7);
    return {
      from: from.toISOString().split("T")[0],
      to: to.toISOString().split("T")[0],
    };
  };

  const defaultDates = getDefaultDates();
  const today = new Date().toISOString().split("T")[0];

  const [siteOptions, setSiteOptions] = useState<SiteOption[]>([]);
  const [sitesLoading, setSitesLoading] = useState(true);

  const [filters, setFilters] = useState<Filters>({
    from: searchParams.get("from") || defaultDates.from,
    to: searchParams.get("to") || defaultDates.to,
    last4: searchParams.get("last4") || "",
    siteId: searchParams.get("siteId") || "",
    deviceId: searchParams.get("deviceId") || "",
    minAmount: searchParams.get("minAmount") || "",
    maxAmount: searchParams.get("maxAmount") || "",
    page: parseInt(searchParams.get("page") || "1"),
    pageSize: parseInt(searchParams.get("pageSize") || "50"),
    sort: searchParams.get("sort") || "transactionTime:desc",
  });

  // Separate state for applied filters (what's actually sent to API)
  const [appliedFilters, setAppliedFilters] = useState<Filters>(filters);

  const [sales, setSales] = useState<SalesRecord[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 50,
    totalRecords: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<SalesRecord | null>(null);

  const updateURL = useCallback(
    (newFilters: Filters) => {
      const params = new URLSearchParams();
      Object.entries(newFilters).forEach(([key, value]) => {
        if (value && value !== "") params.set(key, value.toString());
      });
      setSearchParams(params);
    },
    [setSearchParams]
  );

  useEffect(() => {
    setSitesLoading(true);
    axiosInstance
      .get<Location[]>("/location")
      .then((response) => {
        const options = response.data
          .map((loc) => ({
            name: (loc.name || "").trim(),
            locationPin: (loc.locationPin || "").trim(),
          }))
          .filter((loc) => Boolean(loc.name) && Boolean(loc.locationPin))
          .sort((a, b) => a.name.localeCompare(b.name));
        setSiteOptions(options);
      })
      .catch((err) => {
        console.error("Failed to load locations:", err);
        setSiteOptions([]);
      })
      .finally(() => setSitesLoading(false));
  }, []);

  const getSiteName = useCallback(
    (machineName: string): string => {
      if (!machineName) return "Unknown";
      const lower = machineName.toLowerCase();
      const match = siteOptions.find((site) =>
        lower.includes(site.locationPin.toLowerCase())
      );
      return match?.name || "Unknown";
    },
    [siteOptions]
  );

  const fetchSales = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {
        from: `${appliedFilters.from}T00:00:00.000Z`,
        to: `${appliedFilters.to}T23:59:59.999Z`,
        page: appliedFilters.page,
        pageSize: appliedFilters.pageSize,
        sort: appliedFilters.sort,
      };

      if (appliedFilters.last4) params.last4 = appliedFilters.last4;
      if (appliedFilters.deviceId) params.deviceId = appliedFilters.deviceId;
      if (appliedFilters.minAmount && !isNaN(Number(appliedFilters.minAmount))) {
        params.minAmount = parseFloat(appliedFilters.minAmount);
      }
      if (appliedFilters.maxAmount && !isNaN(Number(appliedFilters.maxAmount))) {
        params.maxAmount = parseFloat(appliedFilters.maxAmount);
      }

      // siteId is the locationPin (SQGSXX) — Nayax machine_name is "SQGSXX - …"
      if (appliedFilters.siteId) params.siteId = appliedFilters.siteId;

      const response = await axiosInstance.get<BackendResponse>(
        "/customer-service/sales",
        { params }
      );

      setSales(response.data.items);

      const totalPages = Math.ceil(response.data.total / response.data.pageSize);

      setPagination({
        page: response.data.page,
        pageSize: response.data.pageSize,
        totalRecords: response.data.total,
        totalPages: totalPages > 0 ? totalPages : 1,
      });
    } catch (err: any) {
      setError("Failed to fetch sales data. Please try again.");
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  // Derive site labels from machine_name once locations (or sales) are available
  const salesWithSites = useMemo(
    () =>
      sales.map((item) => ({
        ...item,
        site: getSiteName(item.machine_name),
      })),
    [sales, getSiteName]
  );

  // Only fetch when appliedFilters changes (not when user is typing)
  useEffect(() => {
    fetchSales();
  }, [fetchSales]);

  // Initial load - apply filters from URL
  useEffect(() => {
    setAppliedFilters(filters);
  }, []); // Only on mount

  // Search button - applies the current filter values
  const handleSearch = () => {
    const newFilters = { ...filters, page: 1 };
    setFilters(newFilters);
    setAppliedFilters(newFilters); // This triggers the API call
    updateURL(newFilters);
  };

  const handleReset = () => {
    const defaults: Filters = {
      ...defaultDates,
      last4: "",
      siteId: "",
      deviceId: "",
      minAmount: "",
      maxAmount: "",
      page: 1,
      pageSize: 50,
      sort: "transactionTime:desc",
    };
    setFilters(defaults);
    setAppliedFilters(defaults); // This triggers the API call
    updateURL(defaults);
  };

  const handleSort = (column: string) => {
    const [currentCol, currentDir] = appliedFilters.sort.split(":");
    const newDir =
      currentCol === column && currentDir === "asc" ? "desc" : "asc";
    const newSort = `${column}:${newDir}`;
    const newFilters = { ...appliedFilters, sort: newSort, page: 1 };
    setFilters(newFilters);
    setAppliedFilters(newFilters); // This triggers the API call
    updateURL(newFilters);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;

    const newFilters = { ...appliedFilters, page: newPage };
    setFilters(newFilters);
    setAppliedFilters(newFilters); // This triggers the API call
    updateURL(newFilters);

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDateTime = (isoString: string) => {
    try {
      const date = new Date(isoString);
      return {
        local: date.toLocaleString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        }),
        utc: date.toUTCString(),
      };
    } catch {
      return { local: isoString, utc: isoString };
    }
  };

  const getSortIcon = (column: string) => {
    const [col, dir] = appliedFilters.sort.split(":");
    if (col !== column) return "⇅";
    return dir === "asc" ? "↑" : "↓";
  };

  const handleLast4Change = (value: string) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length <= 4) {
      setFilters((prev) => ({ ...prev, last4: cleaned }));
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const { page, totalPages } = pagination;
    
    if (totalPages <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (page > 3) {
        pages.push('...');
      }
      
      // Show pages around current page
      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (page < totalPages - 2) {
        pages.push('...');
      }
      
      // Always show last page
      pages.push(totalPages);
    }
    
    return pages;
  };

  const activeFilters = [
    appliedFilters.last4,
    appliedFilters.siteId,
    appliedFilters.deviceId,
    appliedFilters.minAmount,
    appliedFilters.maxAmount,
  ].filter(Boolean).length;

  const sortHeader = (column: string, label: string) => {
    const [col, dir] = appliedFilters.sort.split(":");
    return (
      <th
        scope="col"
        aria-sort={col !== column ? "none" : dir === "asc" ? "ascending" : "descending"}
      >
        <button type="button" className="sortable" onClick={() => handleSort(column)}>
          {label} <span aria-hidden="true">{getSortIcon(column)}</span>
        </button>
      </th>
    );
  };

  return (
    <PageContainer>
      <PageHeader title="Sales" subtitle="Search and view Speed Queen transaction records" />

      {error ? <ErrorBanner onRetry={() => fetchSales()}>{error}</ErrorBanner> : null}

      <Card flush>
        <form
          className="sq-toolbar"
          onSubmit={(e) => {
            e.preventDefault();
            handleSearch();
          }}
        >
          <CollapsibleFilters activeCount={activeFilters}>
            <label className="sq-field is-half">
              <span>From</span>
              <input
                type="date"
                className="sq-input"
                value={filters.from}
                max={today}
                onChange={(e) => setFilters((prev) => ({ ...prev, from: e.target.value }))}
              />
            </label>
            <label className="sq-field is-half">
              <span>To</span>
              <input
                type="date"
                className="sq-input"
                value={filters.to}
                max={today}
                onChange={(e) => setFilters((prev) => ({ ...prev, to: e.target.value }))}
              />
            </label>
            <label className="sq-field">
              <span>Site</span>
              <select
                className="sq-input"
                value={filters.siteId}
                onChange={(e) => setFilters((prev) => ({ ...prev, siteId: e.target.value }))}
                disabled={sitesLoading}
              >
                <option value="">{sitesLoading ? "Loading sites…" : "All sites"}</option>
                {siteOptions.map((site) => (
                  <option key={site.locationPin} value={site.locationPin}>
                    {site.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="sq-field is-half">
              <span>Card last 4</span>
              <input
                type="text"
                inputMode="numeric"
                className="sq-input"
                maxLength={4}
                value={filters.last4}
                onChange={(e) => handleLast4Change(e.target.value)}
              />
            </label>
            <label className="sq-field is-half">
              <span>Device ID</span>
              <input
                type="text"
                className="sq-input"
                value={filters.deviceId}
                onChange={(e) => setFilters((prev) => ({ ...prev, deviceId: e.target.value }))}
              />
            </label>
            <label className="sq-field is-half">
              <span>Min £</span>
              <input
                type="number"
                inputMode="decimal"
                className="sq-input"
                step="0.01"
                min="0"
                value={filters.minAmount}
                onChange={(e) => setFilters((prev) => ({ ...prev, minAmount: e.target.value }))}
              />
            </label>
            <label className="sq-field is-half">
              <span>Max £</span>
              <input
                type="number"
                inputMode="decimal"
                className="sq-input"
                step="0.01"
                min="0"
                value={filters.maxAmount}
                onChange={(e) => setFilters((prev) => ({ ...prev, maxAmount: e.target.value }))}
              />
            </label>
            <div className="sq-form-actions">
              <button type="submit" className="btn btn-sm btn-primary">
                Search
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={handleReset}
              >
                Reset
              </button>
            </div>
          </CollapsibleFilters>
        </form>

        {loading ? (
          <LoadingPanel label="Loading sales" rows={8} />
        ) : salesWithSites.length === 0 ? (
          <EmptyState title="No sales match your filters">
            Try adjusting your date range or filters.
          </EmptyState>
        ) : (
          <>
            <div className="sq-table-wrap">
              <table className="sq-table is-stacked">
                <thead>
                  <tr>
                    {sortHeader("transactionTime", "Time")}
                    <th scope="col">Site</th>
                    {sortHeader("device_id", "Device ID")}
                    {sortHeader("machine_name", "Machine")}
                    {sortHeader("amount", "Amount")}
                    <th scope="col">Tender type</th>
                    <th scope="col">Status</th>
                    <th scope="col">Last 4</th>
                    <th scope="col">Auth code</th>
                  </tr>
                </thead>
                <tbody>
                  {salesWithSites.map((record, index) => (
                    <tr
                      key={record.id || index}
                      className="is-clickable"
                      {...activatable(() => setSelectedRecord(record))}
                    >
                      <td
                        data-label="Time"
                        className="nowrap"
                        title={formatDateTime(record.transactionTime).utc}
                      >
                        {formatDateTime(record.transactionTime).local}
                      </td>
                      <td className="cell-title sq-strong">{record.site || "N/A"}</td>
                      <td data-label="Device ID" className="sales-hide-phone">
                        {record.device_id}
                      </td>
                      <td data-label="Machine">{record.machine_name}</td>
                      <td data-label="Amount" className="nowrap sq-strong">
                        {formatCurrency(record.amount)}
                      </td>
                      <td data-label="Tender type" className="sales-hide-phone">
                        {record.tenderType}
                      </td>
                      <td data-label="Status">
                        <Pill tone={salesStatusTone(record.status)}>{record.status}</Pill>
                      </td>
                      <td data-label="Last 4">****{record.last4}</td>
                      <td data-label="Auth code" className="sales-hide-phone">
                        {record.authCode}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="sq-pagination">
              <span>
                Showing {(pagination.page - 1) * pagination.pageSize + 1} to{" "}
                {Math.min(pagination.page * pagination.pageSize, pagination.totalRecords)} of{" "}
                {pagination.totalRecords} records
              </span>
              <div className="sq-page-btns">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1 || loading}
                >
                  Previous
                </button>
                <span className="sales-page-numbers">
                  {getPageNumbers().map((pageNum, idx) =>
                    typeof pageNum === "number" ? (
                      <button
                        key={idx}
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        aria-current={pagination.page === pageNum ? "page" : undefined}
                        onClick={() => handlePageChange(pageNum)}
                        disabled={loading}
                      >
                        {pageNum}
                      </button>
                    ) : (
                      <span key={idx} aria-hidden="true">
                        {pageNum}
                      </span>
                    )
                  )}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages || loading}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </Card>

      <Drawer
        anchor={isPhone ? "bottom" : "right"}
        open={!!selectedRecord}
        onClose={() => setSelectedRecord(null)}
        PaperProps={{ className: "sales-drawer" }}
      >
        {selectedRecord ? (
          <>
            <header className="sales-drawer-head">
              <h2>Transaction details</h2>
              <button
                type="button"
                className="sq-icon-btn"
                aria-label="Close"
                onClick={() => setSelectedRecord(null)}
              >
                <IconX size={20} />
              </button>
            </header>
            <div className="sales-drawer-body">
              <section className="sq-form-section">
                <h3>Transaction</h3>
                <dl className="sq-dl-rows">
                  <div>
                    <dt>Transaction ID</dt>
                    <dd>{selectedRecord.id}</dd>
                  </div>
                  <div>
                    <dt>Time (local)</dt>
                    <dd>{formatDateTime(selectedRecord.transactionTime).local}</dd>
                  </div>
                  <div>
                    <dt>Time (UTC)</dt>
                    <dd>{formatDateTime(selectedRecord.transactionTime).utc}</dd>
                  </div>
                  <div>
                    <dt>Receipt no</dt>
                    <dd>{selectedRecord.receiptNo}</dd>
                  </div>
                  <div>
                    <dt>Status</dt>
                    <dd>
                      <Pill tone={salesStatusTone(selectedRecord.status)}>
                        {selectedRecord.status}
                      </Pill>
                    </dd>
                  </div>
                </dl>
              </section>
              <section className="sq-form-section">
                <h3>Location</h3>
                <dl className="sq-dl-rows">
                  <div>
                    <dt>Site</dt>
                    <dd>
                      {selectedRecord.site || getSiteName(selectedRecord.machine_name) || "N/A"}
                    </dd>
                  </div>
                  <div>
                    <dt>Device ID</dt>
                    <dd>{selectedRecord.device_id}</dd>
                  </div>
                  <div>
                    <dt>Machine name</dt>
                    <dd>{selectedRecord.machine_name}</dd>
                  </div>
                </dl>
              </section>
              <section className="sq-form-section">
                <h3>Payment</h3>
                <dl className="sq-dl-rows">
                  <div>
                    <dt>Amount</dt>
                    <dd>{formatCurrency(selectedRecord.amount)}</dd>
                  </div>
                  <div>
                    <dt>Tender type</dt>
                    <dd>{selectedRecord.tenderType}</dd>
                  </div>
                  <div>
                    <dt>Card last 4</dt>
                    <dd>****{selectedRecord.last4}</dd>
                  </div>
                  <div>
                    <dt>Auth code</dt>
                    <dd>{selectedRecord.authCode}</dd>
                  </div>
                </dl>
              </section>
            </div>
          </>
        ) : null}
      </Drawer>
    </PageContainer>
  );
};

export default CustomerServiceSales;
